/**
 * The changed-row treatment for curriculum diffs, in ONE place.
 *
 * WHY THIS FILE EXISTS (feat-270). These three were module-private in
 * `QuizContentManagement.tsx`, where the History slide-over was their only
 * consumer. feat-270's approval queue is a second consumer, and `design` ruled
 * that it reuses them **verbatim** so the queue looks like the slide-over it
 * sits beside. "Verbatim" by copy is a promise; "verbatim" by import is a fact,
 * so they moved here and `QuizContentManagement.tsx` imports them back. There is
 * still exactly one definition of each, and the two surfaces cannot drift.
 *
 * ⚠ WHAT DELIBERATELY DID **NOT** MOVE HERE, and this is the load-bearing half.
 * `changedSnapshotFields`, `criteriaChanged` and `formatSnapshotValue`-as-a-
 * comparator stay in `QuizContentManagement.tsx`. They are the History
 * slide-over's own CLIENT-SIDE comparison, and feat-270 must not acquire one:
 * on the approval surface the SERVER decides what counts as changed
 * (`fields[].changed`, ADR-B §2.1, spec AT-13b), because a client twin of that
 * rule is a second expression of one decision and the two disagree the moment
 * normalization is involved. The architect named this exact import as the most
 * likely way a client comparator walks back into the feature — you copy the
 * styling you were told to copy, and the comparator rides along in the same
 * line. **Presentation is shared; the decision is not.** Nothing in this file
 * compares anything, and nothing added to it may.
 *
 * The original design ruling these carry (2026-07-18) is preserved verbatim
 * below, because it is the reason the treatment is neutral rather than amber and
 * it is re-litigated roughly once a feature.
 */

import React from 'react';

/**
 * The changed-row treatment, in ONE place (design-lead ruling, 2026-07-18).
 *
 * NEUTRAL emphasis, deliberately NOT a status colour. `warning` is the
 * caution/status family here (non-prod env, Square needs-reauth, "Draft — not
 * visible"), and a changed field in a routine audit diff is not a caution. It's
 * also frequent rather than rare — `updated` differs on EVERY update — so amber
 * would both dilute the real caution signal and read alarming when many rows
 * light at once. The slate/`preview` family is likewise avoided: it carries
 * "seeded/estimated", a different specific meaning.
 *
 * So the eye is guided by STRUCTURE (a left rail) and the label marker, not by
 * an alarm hue. Neutral `cream.dark` + `inkMuted` collide with nothing.
 *
 * Static literal classes only: a constructed `bg-${x}` is silently purged by
 * Tailwind and renders unstyled.
 */
export const CHANGED_FIELD_ROW_CLASS =
  'bg-cream-dark/60 border-l-2 border-inkMuted pl-1.5 -ml-0.5';

/**
 * The marker span's class string, shared by both surfaces.
 *
 * Split out from `ChangedMarker` for feat-270, which needs the identical
 * treatment under a different WORD. `design/NOTES.md` §8: the History panel says
 * *"· changed"* and is right to — it describes one edit's delta. The approval
 * queue compares the approved snapshot to the live row, which is CUMULATIVE
 * (after three edits it shows approved → current, not edit 3 — AT-26, ADR-B
 * §3.2 ruling 2), so it says *"· differs"*: a state word about two columns, not
 * an event word about an edit. §8 is explicit that the *class* is still shared —
 * *"that is a class name, not copy, and forking it would fork the treatment."*
 * Hence one constant, two words.
 */
export const DIFF_MARKER_CLASS = 'text-inkMuted font-semibold whitespace-nowrap';

/** The label marker: a leading dot plus the word "changed". Both survive
 *  greyscale, so the signal never depends on perceiving colour (CVD-safe) —
 *  which is also what lets the tint itself stay this quiet. */
export const ChangedMarker: React.FC = () => (
  <span className={DIFF_MARKER_CLASS}>· changed</span>
);

/** Leading dot for a changed row's label. */
export const ChangedDot: React.FC = () => (
  <span className="text-inkMuted leading-none" aria-hidden>
    ●
  </span>
);
